// "Prices waiting for your OK" in Review (estimates.awaitingPriceOk, Q44): each estimate with option
// lines a technician priced below their limit, below cost or under the floor, who built it, and how
// far under the price list each line is; why it waits only where the server sends it (cost-seeing
// roles). OK an option's prices here (estimates.approvePrice) or open the job to look first.

import { useEffect, useState } from 'react';
import { client } from '../api.ts';
import { MIRROR_LINE } from '../shared/mirror.ts';
import { errorText } from '../books/ui.tsx';
import { openJob } from '../job/job-page.ts';
import { useDesign } from '../blueprint/design.ts';
import {
  cutText,
  PRICE_OK_FIRST,
  PRICE_OK_MAX,
  signedWaitingText,
  waitingLineText,
  waitingMarginText,
  waitingSummary,
  type WaitingEstimate,
} from './price-ok-model.ts';

export function PriceOksSection({
  tick,
  timeZone,
  canOk,
}: {
  tick: number;
  timeZone: string;
  /** False while the company mirrors ServiceTitan: its estimates' prices are OK'd there. */
  canOk: boolean;
}) {
  const bp = useDesign() === 'blueprint';
  const [es, setEs] = useState<WaitingEstimate[] | null>(null);
  const [total, setTotal] = useState(0);
  // "Show all" reads every one the server will give (it judges them all, so the total is true)
  const [limit, setLimit] = useState(PRICE_OK_FIRST);
  const [again, setAgain] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [why, setWhy] = useState<Record<string, string>>({});
  const [said, setSaid] = useState<string | null>(null);
  useEffect(() => {
    void client
      .call<{ estimates: WaitingEstimate[]; total: number }>('estimates.awaitingPriceOk', { limit })
      .then((r) => {
        setEs(r.estimates);
        setTotal(r.total);
      })
      .catch(() => setEs([]));
  }, [tick, again, limit]);
  // Nothing waits: the section stays out of the way.
  if (es === null || (es.length === 0 && !said)) return null;
  const cut = cutText(es.length, total);
  const ok = async (e: WaitingEstimate, optionId: string, title: string) => {
    setBusy(optionId);
    setSaid(null);
    try {
      await client.call('estimates.approvePrice', { estimateId: e.estimateId, optionId });
      setWhy(({ [optionId]: _, ...rest }) => rest);
      const signed = e.options.find((x) => x.optionId === optionId)?.signed;
      setSaid(
        signed
          ? `The prices on “${title}” (estimate ${e.number}) are OK’d, and the sale ${signed.signerName} signed went through.`
          : `The prices on “${title}” (estimate ${e.number}) are OK’d: it can be sent.`,
      );
      setAgain((n) => n + 1);
    } catch (err) {
      setWhy((w) => ({ ...w, [optionId]: errorText(err) }));
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className="sect price-oks" aria-label="Prices waiting for your OK">
      <h2>Prices waiting for your OK</h2>
      {said && (
        <p className="notice ok small" role="status">
          {said}
        </p>
      )}
      {es.length > 0 && <p className="muted small">{waitingSummary(es)}</p>}
      {/* the summary counts what's shown: past the first page, it says how many wait in all */}
      {cut && (
        <p className="muted small">
          {cut}{' '}
          {limit < PRICE_OK_MAX && (
            <button
              type="button"
              className={bp ? 'ib-link' : 'btn quiet sm'}
              onClick={() => setLimit(PRICE_OK_MAX)}
            >
              {total <= PRICE_OK_MAX ? `Show all ${total}` : `Show ${PRICE_OK_MAX}`}
            </button>
          )}
        </p>
      )}
      {!canOk && <p className="muted small">{MIRROR_LINE}</p>}
      <ul className="rows price-ok-list">
        {es.map((e) => (
          <li key={e.estimateId} className="stone price-ok">
            <div className="row between">
              <b>
                #{e.jobNumber} · {e.customerName}
              </b>
              <button type="button" className="btn quiet sm" onClick={() => openJob(e.jobId)}>
                Open job
              </button>
            </div>
            <span className="muted small block">
              Estimate {e.number}: {e.title}
              {e.techName ? ` · built by ${e.techName}` : ''}
            </span>
            {e.options.map((o) => (
              <div key={o.optionId} className="price-ok-option">
                <span className="small">
                  <b>{o.title}</b>
                </span>
                <ul className="small">
                  {o.lines.map((l) => (
                    <li key={l.lineId}>{waitingLineText(l)}</li>
                  ))}
                  {o.margin && <li>{waitingMarginText(o.margin)}</li>}
                </ul>
                {o.signed && (
                  <p className="small price-ok-signed">
                    <b>{signedWaitingText(o.signed, timeZone)}</b>
                  </p>
                )}
                {canOk && (
                  <button
                    type="button"
                    className="btn sm"
                    disabled={busy !== null}
                    aria-label={`OK the prices on ${o.title}, estimate ${e.number}`}
                    onClick={() => void ok(e, o.optionId, o.title)}
                  >
                    {busy === o.optionId ? 'OK’ing…' : 'OK these prices'}
                  </button>
                )}
                {why[o.optionId] && (
                  <p className="error small" role="alert">
                    {why[o.optionId]}
                  </p>
                )}
              </div>
            ))}
          </li>
        ))}
      </ul>
    </section>
  );
}
